import { API_URL } from '@/config';
import { formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, User, ArrowLeft, Image as ImageIcon, CheckCircle, XCircle, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [convener, setConvener] = useState(null);
  const [coConvener, setCoConvener] = useState(null);
  const [chairman, setChairman] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myConfirmation, setMyConfirmation] = useState(null);
  const [confirmationCount, setConfirmationCount] = useState(0);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  useEffect(() => {
    loadEventData();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [eventRes, mediaRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, { headers }),
        axios.get(`${API}/media?event_id=${eventId}&status=approved`, { headers })
      ]);
      setEvent(eventRes.data);
      setMedia(mediaRes.data);
      
      // Load RSVP status if confirmation is required
      if (eventRes.data.confirmation_required) {
        try {
          const [myConfRes, confirmationsRes] = await Promise.all([
            axios.get(`${API}/events/${eventId}/my-confirmation`, { headers }),
            axios.get(`${API}/events/${eventId}/confirmations`, { headers })
          ]);
          setMyConfirmation(myConfRes.data.confirmed ? myConfRes.data.confirmation : null);
          setConfirmationCount(confirmationsRes.data.total_confirmations || 0);
        } catch (e) {
          console.log('RSVP check failed:', e);
        }
      }
      
      if (eventRes.data.convener_member_id) {
        const convenerRes = await axios.get(`${API}/members/${eventRes.data.convener_member_id}`, { headers });
        setConvener(convenerRes.data);
      }
      if (eventRes.data.co_convener_member_id) {
        const coConvenerRes = await axios.get(`${API}/members/${eventRes.data.co_convener_member_id}`, { headers });
        setCoConvener(coConvenerRes.data);
      }
      if (eventRes.data.chairman_member_id) {
        const chairmanRes = await axios.get(`${API}/members/${eventRes.data.chairman_member_id}`, { headers });
        setChairman(chairmanRes.data);
      }
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async () => {
    setRsvpLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.post(`${API}/events/${eventId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RSVP confirmed! See you at the event.');
      loadEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleCancelRSVP = async () => {
    if (!window.confirm('Are you sure you want to cancel your RSVP?')) return;
    
    setRsvpLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      await axios.delete(`${API}/events/${eventId}/confirm`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RSVP cancelled');
      setMyConfirmation(null);
      loadEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return <div className="text-center py-12"><p>Event not found</p></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="event-detail-page">
      <Link to="/app/events">
        <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Back to Events</Button>
      </Link>
      
      <Card>
        {event.banner_image_url && (
          <div className="h-64 md:h-96 overflow-hidden rounded-t-xl">
            <img src={event.banner_image_url} alt={event.title} className="w-full h-full object-cover" />
          </div>
        )}
        <CardContent className="p-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0F172A' }}>
            {event.title}
          </h1>
          
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-5 h-5" />
              <span>{formatDateTime(event.date_time_start)}</span>
            </div>
            {event.venue && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-5 h-5" />
                <span>{event.venue}</span>
              </div>
            )}
          </div>

          {/* RSVP Section */}
          {event.confirmation_required && (
            <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">RSVP Required</p>
                    <p className="text-sm text-purple-700">{confirmationCount} member{confirmationCount !== 1 ? 's' : ''} confirmed</p>
                  </div>
                </div>
                
                {myConfirmation ? (
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      You're attending
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCancelRSVP}
                      disabled={rsvpLoading}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid="cancel-rsvp-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={handleRSVP}
                    disabled={rsvpLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="rsvp-btn"
                  >
                    {rsvpLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Confirming...
                      </span>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm RSVP
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {(convener || coConvener || chairman) && (
            <div className="flex flex-wrap gap-3 mb-6">
              {convener && (
                <Link to={`/app/members/${convener.user_id}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-accent hover:bg-accent/80 transition-all">
                  <User className="w-4 h-4" />
                  <span className="text-sm"><span className="font-medium">Convener:</span> {convener.name}</span>
                </Link>
              )}
              {coConvener && (
                <Link to={`/app/members/${coConvener.user_id}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-accent hover:bg-accent/80 transition-all">
                  <User className="w-4 h-4" />
                  <span className="text-sm"><span className="font-medium">Co-Convener:</span> {coConvener.name}</span>
                </Link>
              )}
              {chairman && (
                <Link to={`/app/members/${chairman.user_id}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-accent hover:bg-accent/80 transition-all">
                  <User className="w-4 h-4" />
                  <span className="text-sm"><span className="font-medium">Chairman:</span> {chairman.name}</span>
                </Link>
              )}
            </div>
          )}
          
          {event.description && (
            <div className="prose max-w-none">
              <p className="text-muted-foreground leading-relaxed">{event.description}</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {media.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Event Gallery
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((item) => (
                <div key={item.media_id} className="aspect-square rounded-lg overflow-hidden hover-lift">
                  {item.type === 'photo' ? (
                    <img src={getImageUrl(item.url)} alt={item.caption || 'Event media'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-royal flex items-center justify-center">
                      <span className="text-white text-4xl">▶</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EventDetailPage;
